// components/RfiManager.jsx — RFIs for the drywall trade.
//   • Suggestions found for you (no AI): wall tags with no assembly detail, assemblies missing gauge / spacing /
//     height / board, untagged walls, tag ≠ wall drawn, openings to confirm (from the takeoff) and spec items
//     (Level 5 locations, delegated design, mock-ups … + the scanner's AI summaries). "+ Add" puts them on an RFI —
//     and drops a pin where it is on the drawing.
//   • Write / edit the RFI, send it (opens your e-mail with the questions; Print / PDF for the formal copy),
//     record the answers question by question, keep a log; overdue RFIs stand out.
//
// Props: projectId | opportunityId, ownerLabel, rfis, setRfis (the host keeps them: the drawing shows the pins),
//        getDrawing() → the takeoff payload (or null), takeoffId, inbox [items from the spec scanner],
//        onInboxTaken(keys), onShowPin(pin), onPlacePin({ rfiId, itemId }), reportMeta, onBack

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { drawingSuggestions } from "../lib/takeoff/rfiSuggest";
import { mailtoUrl, printRfi, rfiNo } from "../lib/takeoff/rfiReport";
import {
  ImageMarkup,
  RfiImage,
  uploadRfiImage,
  deleteRfiImage,
  captionRfiImage,
  imagesForPrint,
  pictureFrom,
  storedImageUrl,
} from "./RfiImageTools";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-Tenant-ID": localStorage.getItem("tenantId"),
});
const cls = (...c) => c.filter(Boolean).join(" ");
const rid = () => Math.random().toString(36).slice(2, 10);
const errText = (e) =>
  e?.response?.data?.message || e?.message || "Something went wrong";
const inp =
  "w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-[13px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none";
const dayStr = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const nice = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
// five working days from today
const defaultDue = () => {
  const d = new Date();
  let n = 0;
  while (n < 5) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() % 6) n++;
  }
  return dayStr(d);
};
const STATUS = {
  draft: ["Draft", "bg-gray-500/20 text-gray-300"],
  sent: ["Sent", "bg-sky-500/15 text-sky-300"],
  answered: ["Answered", "bg-emerald-500/15 text-emerald-300"],
  closed: ["Closed", "bg-white/5 text-gray-500"],
  void: ["Void", "bg-red-500/10 text-red-300/70"],
};
const overdue = (r) =>
  r.status === "sent" &&
  r.dueDate &&
  new Date(r.dueDate) < new Date(new Date().toDateString());
const SEV = { high: "bg-red-400", med: "bg-amber-400", low: "bg-gray-500" };

function BigPicture({ im, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    storedImageUrl(im.file)
      .then(setUrl)
      .catch(() => {});
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [im, onClose]);
  return (
    <div
      className="fixed inset-0 z-[2100] flex flex-col items-center justify-center bg-black/85 p-6"
      onClick={onClose}
    >
      {url && (
        <img
          src={url}
          alt=""
          className="max-h-[85vh] max-w-full rounded bg-white shadow-2xl"
        />
      )}
      <div className="mt-2 text-xs text-gray-300">
        {im.caption || ""}
        {im.by ? ` · added by ${im.by}` : ""}
        {im.at ? ` · ${new Date(im.at).toLocaleDateString()}` : ""}
      </div>
    </div>
  );
}

export default function RfiManager({
  projectId,
  opportunityId,
  ownerLabel = "",
  rfis = [],
  setRfis,
  getDrawing,
  takeoffId = null,
  inbox = [],
  onInboxTaken,
  onShowPin,
  onPlacePin,
  reportMeta = {},
  focusRfiId = null, // { id, t } — open this one (a pin was clicked on the drawing)
  onSnip, // ({ rfiId, itemId }) => void — go to the drawing and cut a picture for this question
  onBack,
}) {
  const ownerQ = projectId ? { projectId } : { opportunityId };
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [filter, setFilter] = useState("open");
  const [saveState, setSaveState] = useState("saved");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("drawing");
  const [drawing, setDrawing] = useState([]);
  const [specIdeas, setSpecIdeas] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [respFor, setRespFor] = useState({}); // itemId → text being typed
  const [logText, setLogText] = useState("");
  const saveTimer = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;
  const edits = useRef(0); // edits made since the last save started
  const stateRef = useRef("saved");
  stateRef.current = saveState;
  useEffect(() => {
    if (!focusRfiId?.id) return;
    setSelId(focusRfiId.id);
    setFilter("all");
  }, [focusRfiId]);

  const api = {
    create: (body) =>
      axios
        .post(`${API}/rfis`, { ...ownerQ, ...body }, { headers: authHeaders() })
        .then((r) => r.data.rfi),
    update: (id, body) =>
      axios
        .put(`${API}/rfis/${id}`, body, { headers: authHeaders() })
        .then((r) => r.data.rfi),
    send: (id, body) =>
      axios
        .post(`${API}/rfis/${id}/send`, body, { headers: authHeaders() })
        .then((r) => r.data.rfi),
    respond: (id, body) =>
      axios
        .post(`${API}/rfis/${id}/response`, body, { headers: authHeaders() })
        .then((r) => r.data.rfi),
    note: (id, text) =>
      axios
        .post(`${API}/rfis/${id}/note`, { text }, { headers: authHeaders() })
        .then((r) => r.data.rfi),
    remove: (id) =>
      axios.delete(`${API}/rfis/${id}`, { headers: authHeaders() }),
  };
  const put = (rfi) =>
    setRfis((list) =>
      list.some((x) => x._id === rfi._id)
        ? list.map((x) => (x._id === rfi._id ? rfi : x))
        : [rfi, ...list],
    );

  // pick one
  useEffect(() => {
    if (selId && rfis.some((r) => r._id === selId)) return;
    const first = rfis.find((r) => r.status === "draft") || rfis[0];
    setSelId(first?._id || null);
  }, [rfis]); // eslint-disable-line
  useEffect(() => {
    const r = rfis.find((x) => x._id === selId) || null;
    // (keep what's being typed if it's the same RFI)
    if (!r) setDraft(null);
    else if (
      !draftRef.current ||
      draftRef.current._id !== r._id ||
      saveState === "saved"
    )
      setDraft(JSON.parse(JSON.stringify(r)));
  }, [selId, rfis]); // eslint-disable-line

  // suggestions
  const refreshDrawing = () =>
    setDrawing(drawingSuggestions(getDrawing?.() || null));
  useEffect(() => {
    refreshDrawing();
    // spec ideas: every spec of this project — the checks found when it was read + the AI summaries' questions
    (async () => {
      try {
        const { specs = [] } = (
          await axios.get(`${API}/specs`, {
            headers: authHeaders(),
            params: ownerQ,
          })
        ).data;
        const ideas = [];
        for (const s of specs.filter((x) => x.status === "ready").slice(0, 4)) {
          const { spec } = (
            await axios.get(`${API}/specs/${s._id}`, { headers: authHeaders() })
          ).data;
          (spec.checks || []).forEach((c, i) =>
            ideas.push({
              ...c,
              key: `spec:${spec._id}:ck:${c.ref}:${i}`,
              specId: spec._id,
              specName: spec.name,
            }),
          );
          (spec.overview?.rfis || []).forEach((c, i) =>
            ideas.push({
              ...c,
              key: `spec:${spec._id}:ov:${i}`,
              specId: spec._id,
              specName: spec.name,
            }),
          );
          for (const sec of spec.sections || [])
            (sec.summary?.rfis || []).forEach((c, i) =>
              ideas.push({
                ...c,
                ref: c.ref ? `${sec.num} §${c.ref}` : sec.num,
                section: sec.num,
                key: `spec:${spec._id}:${sec.num}:${i}`,
                specId: spec._id,
                specName: spec.name,
              }),
            );
        }
        setSpecIdeas(ideas);
      } catch {
        /* no specs yet */
      }
    })();
  }, [projectId, opportunityId]); // eslint-disable-line

  const usedKeys = useMemo(
    () =>
      new Set(
        rfis.flatMap((r) =>
          (r.items || []).map((i) => i.source?.key).filter(Boolean),
        ),
      ),
    [rfis],
  );

  // ── edits (saved a moment after you stop typing) ──
  const edit = (patch) => {
    edits.current++;
    setDraft((d) => ({ ...d, ...patch }));
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 800);
  };
  const save = async () => {
    clearTimeout(saveTimer.current);
    const d = draftRef.current;
    if (!d?._id) return;
    setSaveState("saving");
    const n = edits.current;
    try {
      const {
        title,
        to,
        cc,
        dueDate,
        priority,
        costImpact,
        scheduleImpact,
        message,
        items,
        pins,
      } = d;
      const saved = await api.update(d._id, {
        title,
        to,
        cc,
        dueDate,
        priority,
        costImpact,
        scheduleImpact,
        message,
        items,
        pins,
      });
      // typed more while saving: keep the screen's copy, the next save sends it
      if (edits.current === n) {
        put(saved);
        setSaveState("saved");
      } else setSaveState("dirty");
    } catch (e) {
      setSaveState("error");
      setErr(errText(e));
    }
  };
  // leaving the RFIs (to the drawing, the spec…) with an edit not yet saved: save it now
  useEffect(
    () => () => {
      clearTimeout(saveTimer.current);
      if (stateRef.current !== "saved") save();
    },
    [], // eslint-disable-line
  );
  const setItem = (id, patch) =>
    edit({
      items: draft.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    });
  const removeItem = (id) =>
    edit({
      items: draft.items.filter((i) => i.id !== id),
      pins: (draft.pins || []).filter((p) => p.itemId !== id),
    });
  const moveItem = (id, dir) => {
    const items = [...draft.items];
    const k = items.findIndex((i) => i.id === id);
    const j = k + dir;
    if (j < 0 || j >= items.length) return;
    [items[k], items[j]] = [items[j], items[k]];
    edit({ items });
  };

  const newRfi = async (items = [], pins = []) => {
    setErr("");
    try {
      if (draftRef.current && saveState !== "saved") await save();
      const rfi = await api.create({
        title: "Drywall clarifications",
        items,
        pins,
        dueDate: defaultDue(),
      });
      put(rfi);
      setSelId(rfi._id);
      setFilter("open");
      return rfi;
    } catch (e) {
      setErr(errText(e));
      return null;
    }
  };

  // a suggestion → an item (and a pin where it is on the drawing) on the open draft, else on a new RFI
  const addSuggestion = async (sug) => {
    const item = {
      id: rid(),
      question: sug.question,
      assumption: sug.assumption || "",
      source: { ...(sug.source || {}), key: sug.key },
      response: "",
      answered: false,
    };
    const pin =
      sug.pin && takeoffId
        ? {
            id: rid(),
            takeoffId,
            page: sug.pin.page,
            lat: sug.pin.lat,
            lng: sug.pin.lng,
            itemId: item.id,
          }
        : null;
    if (draft && draft.status === "draft") {
      edit({
        items: [...draft.items, item],
        pins: pin ? [...(draft.pins || []), pin] : draft.pins || [],
      });
      setNote(
        `Added to ${rfiNo(draft)}${pin ? " and pinned on the drawing" : ""}`,
      );
    } else {
      const r = await newRfi([item], pin ? [pin] : []);
      if (r)
        setNote(
          `New ${rfiNo(r)} started${pin ? " — pinned on the drawing" : ""}`,
        );
    }
  };
  const addBlank = () => {
    if (!draft || draft.status !== "draft")
      return newRfi([
        {
          id: rid(),
          question: "",
          assumption: "",
          source: { kind: "manual" },
          response: "",
          answered: false,
        },
      ]);
    edit({
      items: [
        ...draft.items,
        {
          id: rid(),
          question: "",
          assumption: "",
          source: { kind: "manual" },
          response: "",
          answered: false,
        },
      ],
    });
  };

  // ── send / answers / status ──
  const doSend = async ({ openMail }) => {
    await save();
    try {
      const d = draftRef.current;
      const rfi = await api.send(d._id, {
        to: d.to,
        cc: d.cc,
        dueDate: d.dueDate,
        message: d.message,
      });
      put(rfi);
      setDraft(JSON.parse(JSON.stringify(rfi)));
      setSendOpen(false);
      if (openMail)
        window.location.href = mailtoUrl(rfi, {
          ...reportMeta,
          project: reportMeta.project || ownerLabel,
        });
      setNote(`${rfiNo(rfi)} marked as sent`);
    } catch (e) {
      setErr(errText(e));
    }
  };
  const doPrint = async () => {
    // open the window on the click (pop-up blockers), fill it once the pictures are loaded
    const win = window.open("", "_blank");
    if (!win)
      return setErr(
        "The browser blocked the window — allow pop-ups for this site",
      );
    win.document.write(
      '<p style="font:14px sans-serif;padding:24px;color:#555">Preparing the RFI…</p>',
    );
    await save();
    const images = await imagesForPrint(draftRef.current);
    printRfi(
      draftRef.current,
      { ...reportMeta, project: reportMeta.project || ownerLabel },
      images,
      win,
    );
  };

  // ── pictures ──
  const [markup, setMarkup] = useState(null); // { src, itemId }
  const [savingPic, setSavingPic] = useState(false);
  const [big, setBig] = useState(null); // file shown large
  const fileInput = useRef(null);
  const pickFor = useRef(null);
  const canPicture = draft && !["closed", "void"].includes(draft.status);
  const startPicture = async (itemId, fileLike) => {
    const src = await pictureFrom(fileLike);
    if (!src) return setErr("That isn't an image (JPEG, PNG or WebP)");
    setErr("");
    setMarkup({ src, itemId });
  };
  // merge the server's pictures into the screen's copy (without touching what's being typed)
  const takeImages = (saved) => {
    put(saved);
    setDraft((d) =>
      d && d._id === saved._id
        ? {
            ...d,
            items: d.items.map((i) => ({
              ...i,
              images: saved.items.find((x) => x.id === i.id)?.images || [],
            })),
          }
        : d,
    );
  };
  const savePicture = async ({ blob, w, h, caption }) => {
    setSavingPic(true);
    try {
      await save(); // the question must be on the server before its picture
      takeImages(
        await uploadRfiImage(draft._id, markup.itemId, blob, {
          w,
          h,
          caption,
          source: "upload",
        }),
      );
      setMarkup(null);
      setNote("Picture added");
    } catch (e) {
      setErr(errText(e));
    } finally {
      setSavingPic(false);
    }
  };
  const removePicture = async (im) => {
    if (!window.confirm("Remove this picture?")) return;
    try {
      takeImages(await deleteRfiImage(draft._id, im.id));
    } catch (e) {
      setErr(errText(e));
    }
  };
  const editCaption = async (im) => {
    const c = window.prompt("Caption", im.caption || "");
    if (c == null) return;
    try {
      takeImages(await captionRfiImage(draft._id, im.id, c));
    } catch (e) {
      setErr(errText(e));
    }
  };
  // Ctrl+V a screenshot / drop a file on a question
  const onPaste = (itemId) => (e) => {
    const it = [...(e.clipboardData?.items || [])].find(
      (x) => x.kind === "file" && /^image\//.test(x.type),
    );
    if (!it || !canPicture) return;
    e.preventDefault();
    startPicture(itemId, it);
  };
  const onDrop = (itemId) => (e) => {
    const f = [...(e.dataTransfer?.files || [])].find((x) =>
      /^image\//.test(x.type),
    );
    if (!f || !canPicture) return;
    e.preventDefault();
    startPicture(itemId, f);
  };
  const saveResponse = async (itemId) => {
    const text = (respFor[itemId] ?? "").trim();
    if (!text) return;
    try {
      const rfi = await api.respond(draft._id, { itemId, text });
      put(rfi);
      setDraft(JSON.parse(JSON.stringify(rfi)));
      setRespFor((p) => ({ ...p, [itemId]: undefined }));
    } catch (e) {
      setErr(errText(e));
    }
  };
  const addLog = async (kind) => {
    const text = logText.trim();
    if (!text) return;
    try {
      const rfi =
        kind === "response"
          ? await api.respond(draft._id, { text, answered: true })
          : await api.note(draft._id, text);
      put(rfi);
      setDraft(JSON.parse(JSON.stringify(rfi)));
      setLogText("");
    } catch (e) {
      setErr(errText(e));
    }
  };
  const setStatus = async (status) => {
    await save();
    try {
      const rfi = await api.update(draft._id, { status });
      put(rfi);
      setDraft(JSON.parse(JSON.stringify(rfi)));
    } catch (e) {
      setErr(errText(e));
    }
  };
  const remove = async () => {
    if (
      !window.confirm(
        draft.status === "draft"
          ? `Delete ${rfiNo(draft)}?`
          : `Void ${rfiNo(draft)}? It keeps its number and history.`,
      )
    )
      return;
    try {
      await api.remove(draft._id);
      if (draft.status === "draft")
        setRfis((l) => l.filter((x) => x._id !== draft._id));
      else put({ ...draft, status: "void" });
      setSelId(null);
    } catch (e) {
      setErr(errText(e));
    }
  };

  const shown = rfis.filter((r) =>
    filter === "all"
      ? true
      : filter === "open"
        ? ["draft", "sent"].includes(r.status)
        : filter === "answered"
          ? r.status === "answered"
          : ["closed", "void"].includes(r.status),
  );
  const counts = {
    open: rfis.filter((r) => ["draft", "sent"].includes(r.status)).length,
    overdue: rfis.filter(overdue).length,
  };
  const editable = draft && draft.status === "draft";
  const pinsOf = (itemId) =>
    (draft?.pins || []).filter((p) => p.itemId === itemId);
  const ideas =
    tab === "drawing"
      ? drawing
      : tab === "spec"
        ? specIdeas.map((x) => ({
            key: x.key,
            question: x.question,
            why: x.why,
            severity: "med",
            source: {
              kind: "spec",
              label: `Spec ${x.ref || x.section || ""}`.trim(),
              page: x.page || null,
              specId: x.specId,
              section: x.section || "",
            },
          }))
        : inbox;

  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-950 text-gray-100">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-gray-900/80 px-4 py-3">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/10"
          >
            ← Back
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-white">RFIs</h1>
          {ownerLabel && (
            <div className="truncate text-xs text-gray-500">{ownerLabel}</div>
          )}
        </div>
        <span className="text-xs text-gray-400">
          {counts.open} open
          {counts.overdue ? (
            <span className="ml-1 text-red-300">
              · {counts.overdue} overdue
            </span>
          ) : null}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {err && <span className="text-xs text-red-300">{err}</span>}
          {!err && note && (
            <span className="text-xs text-emerald-300">{note}</span>
          )}
          <button
            onClick={() => newRfi([])}
            className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
          >
            + New RFI
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[260px_1fr_360px]">
        {/* list */}
        <aside className="flex min-h-0 flex-col border-r border-white/10">
          <div className="flex gap-1 border-b border-white/10 p-2 text-[11px]">
            {[
              ["open", "Open"],
              ["answered", "Answered"],
              ["closed", "Closed"],
              ["all", "All"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={cls(
                  "rounded-md px-2.5 py-1 font-medium",
                  filter === k
                    ? "bg-orange-500 text-white"
                    : "text-gray-400 hover:bg-white/5",
                )}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!shown.length && (
              <p className="p-4 text-xs text-gray-500">
                No RFIs here. Start one from the suggestions on the right, or “+
                New RFI”.
              </p>
            )}
            {shown.map((r) => (
              <button
                key={r._id}
                onClick={async () => {
                  if (saveState !== "saved") await save();
                  setSelId(r._id);
                }}
                className={cls(
                  "block w-full border-b border-white/5 px-3 py-2.5 text-left",
                  r._id === selId ? "bg-orange-500/10" : "hover:bg-white/5",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-orange-300">
                    {rfiNo(r)}
                  </span>
                  <span
                    className={cls(
                      "rounded px-1.5 py-px text-[9.5px] font-semibold",
                      STATUS[r.status]?.[1],
                    )}
                  >
                    {STATUS[r.status]?.[0]}
                  </span>
                  {overdue(r) && (
                    <span className="rounded bg-red-500/20 px-1.5 py-px text-[9.5px] font-semibold text-red-300">
                      Overdue
                    </span>
                  )}
                </div>
                <div className="truncate text-[12.5px] text-gray-100">
                  {r.title || "Untitled"}
                </div>
                <div className="text-[10.5px] text-gray-500">
                  {r.items?.length || 0} question
                  {r.items?.length === 1 ? "" : "s"}
                  {r.items?.length
                    ? ` · ${r.items.filter((i) => i.answered).length} answered`
                    : ""}
                  {r.dueDate ? ` · due ${nice(r.dueDate)}` : ""}
                  {r.pins?.length ? ` · 📍${r.pins.length}` : ""}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* editor */}
        <main className="min-h-0 overflow-y-auto p-4">
          {!draft ? (
            <div className="grid h-full place-items-center text-center text-sm text-gray-500">
              <div>
                Pick an RFI, or add a suggestion from the right —<br />
                questions found on the drawings and in the spec.
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-orange-300">
                  {rfiNo(draft)}
                </span>
                <span
                  className={cls(
                    "rounded px-1.5 py-px text-[10px] font-semibold",
                    STATUS[draft.status]?.[1],
                  )}
                >
                  {STATUS[draft.status]?.[0]}
                </span>
                {overdue(draft) && (
                  <span className="rounded bg-red-500/20 px-1.5 py-px text-[10px] font-semibold text-red-300">
                    Overdue
                  </span>
                )}
                <span className="text-[11px] text-gray-500">
                  {saveState === "saving"
                    ? "Saving…"
                    : saveState === "dirty"
                      ? "Unsaved"
                      : saveState === "error"
                        ? "Save failed"
                        : "Saved"}
                </span>
                <div className="ml-auto flex flex-wrap gap-1.5">
                  <button
                    onClick={doPrint}
                    className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                  >
                    Print / PDF
                  </button>
                  {draft.status === "draft" && (
                    <button
                      onClick={() => setSendOpen(true)}
                      disabled={!draft.items.some((i) => i.question.trim())}
                      className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                    >
                      Send…
                    </button>
                  )}
                  {draft.status === "sent" && (
                    <button
                      onClick={() =>
                        (window.location.href = mailtoUrl(draft, {
                          ...reportMeta,
                          project: reportMeta.project || ownerLabel,
                        }))
                      }
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                    >
                      E-mail again
                    </button>
                  )}
                  <select
                    value={draft.status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-xs text-gray-200"
                    title="Status"
                  >
                    {["draft", "sent", "answered", "closed"].map((k) => (
                      <option key={k} value={k}>
                        {STATUS[k][0]}
                      </option>
                    ))}
                    {draft.status === "void" && (
                      <option value="void">Void</option>
                    )}
                  </select>
                  <button
                    onClick={remove}
                    className="rounded-lg px-2 py-1.5 text-xs text-gray-500 hover:bg-red-500/10 hover:text-red-300"
                  >
                    {draft.status === "draft" ? "Delete" : "Void"}
                  </button>
                </div>
              </div>

              <div className="grid gap-3 rounded-2xl border border-white/10 bg-gray-900/70 p-4 sm:grid-cols-6">
                <label className="sm:col-span-6">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Subject
                  </span>
                  <input
                    value={draft.title}
                    onChange={(e) => edit({ title: e.target.value })}
                    className={inp}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    To (name)
                  </span>
                  <input
                    value={draft.to?.name || ""}
                    onChange={(e) =>
                      edit({ to: { ...draft.to, name: e.target.value } })
                    }
                    placeholder="Architect / GC PM"
                    className={inp}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Company
                  </span>
                  <input
                    value={draft.to?.company || ""}
                    onChange={(e) =>
                      edit({ to: { ...draft.to, company: e.target.value } })
                    }
                    className={inp}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    E-mail
                  </span>
                  <input
                    type="email"
                    value={draft.to?.email || ""}
                    onChange={(e) =>
                      edit({ to: { ...draft.to, email: e.target.value } })
                    }
                    className={inp}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Answer needed by
                  </span>
                  <input
                    type="date"
                    value={dayStr(draft.dueDate)}
                    onChange={(e) => edit({ dueDate: e.target.value || null })}
                    className={inp}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Cost impact
                  </span>
                  <select
                    value={draft.costImpact || "unknown"}
                    onChange={(e) => edit({ costImpact: e.target.value })}
                    className={inp}
                  >
                    {["unknown", "none", "possible", "yes"].map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Priority
                  </span>
                  <select
                    value={draft.priority || "normal"}
                    onChange={(e) => edit({ priority: e.target.value })}
                    className={inp}
                  >
                    {["normal", "high", "urgent"].map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-6">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Cover note (optional)
                  </span>
                  <textarea
                    rows={2}
                    value={draft.message || ""}
                    onChange={(e) => edit({ message: e.target.value })}
                    className={inp}
                    placeholder="Please clarify the following for our drywall price…"
                  />
                </label>
              </div>

              <div className="space-y-2">
                {draft.items.map((it, i) => (
                  <div
                    key={it.id}
                    onPaste={onPaste(it.id)}
                    onDragOver={(e) =>
                      canPicture &&
                      e.dataTransfer?.types?.includes("Files") &&
                      e.preventDefault()
                    }
                    onDrop={onDrop(it.id)}
                    className="rounded-2xl border border-white/10 bg-gray-900/70 p-3"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-orange-500 text-[11px] font-bold text-white">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1 space-y-2">
                        <textarea
                          rows={Math.min(
                            6,
                            Math.max(
                              2,
                              Math.ceil((it.question || "").length / 90),
                            ),
                          )}
                          value={it.question}
                          onChange={(e) =>
                            setItem(it.id, { question: e.target.value })
                          }
                          readOnly={!editable}
                          placeholder="The question…"
                          className={cls(
                            inp,
                            !editable && "border-transparent bg-transparent",
                          )}
                        />
                        <div className="flex flex-wrap items-center gap-2 text-[11px]">
                          {it.source?.label && (
                            <span
                              className={cls(
                                "rounded px-1.5 py-0.5",
                                it.source.kind === "spec"
                                  ? "bg-violet-500/15 text-violet-300"
                                  : it.source.kind === "drawing"
                                    ? "bg-sky-500/15 text-sky-300"
                                    : "bg-white/5 text-gray-400",
                              )}
                            >
                              {it.source.kind === "spec"
                                ? "§ "
                                : it.source.kind === "drawing"
                                  ? "▦ "
                                  : ""}
                              {it.source.label}
                            </span>
                          )}
                          {pinsOf(it.id).map((p) => (
                            <button
                              key={p.id}
                              onClick={() => onShowPin?.(p)}
                              className="rounded bg-orange-500/15 px-1.5 py-0.5 text-orange-200 hover:bg-orange-500/25"
                              title="Show on the drawing"
                            >
                              📍 page {p.page}
                            </button>
                          ))}
                          {onPlacePin && editable && (
                            <button
                              onClick={() =>
                                onPlacePin({ rfiId: draft._id, itemId: it.id })
                              }
                              className="rounded bg-white/5 px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                            >
                              + Pin on drawing
                            </button>
                          )}
                          {onSnip && canPicture && (
                            <button
                              onClick={async () => {
                                await save();
                                onSnip({ rfiId: draft._id, itemId: it.id });
                              }}
                              className="rounded bg-white/5 px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                              title="Go to the drawing and drag a box round the area"
                            >
                              ✂ Cut from drawing
                            </button>
                          )}
                          {canPicture && (
                            <button
                              onClick={() => {
                                pickFor.current = it.id;
                                fileInput.current?.click();
                              }}
                              className="rounded bg-white/5 px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                              title="Or paste a screenshot (Ctrl+V) / drop an image on the question"
                            >
                              🖼 Add picture
                            </button>
                          )}
                          {editable && (
                            <span className="ml-auto flex gap-1">
                              <button
                                onClick={() => moveItem(it.id, -1)}
                                className="rounded bg-white/5 px-1.5 text-gray-400 hover:bg-white/10"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveItem(it.id, 1)}
                                className="rounded bg-white/5 px-1.5 text-gray-400 hover:bg-white/10"
                              >
                                ↓
                              </button>
                              <button
                                onClick={() => removeItem(it.id)}
                                className="rounded bg-white/5 px-1.5 text-gray-400 hover:bg-red-500/20 hover:text-red-300"
                              >
                                ✕
                              </button>
                            </span>
                          )}
                        </div>
                        {it.images?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {it.images.map((im, k) => (
                              <div
                                key={im.id}
                                className="group relative w-40 overflow-hidden rounded-lg border border-white/10 bg-black/30"
                              >
                                <RfiImage
                                  file={im.file}
                                  onClick={() => setBig(im)}
                                  className="h-28 w-full cursor-zoom-in object-cover"
                                />
                                <div
                                  className="truncate px-1.5 py-1 text-[10.5px] text-gray-400"
                                  title={im.caption}
                                >
                                  Fig. {i + 1}.{k + 1}
                                  {im.caption
                                    ? ` — ${im.caption}`
                                    : im.source === "drawing" && im.page
                                      ? ` · page ${im.page}`
                                      : ""}
                                </div>
                                {canPicture && (
                                  <div className="absolute right-1 top-1 hidden gap-1 group-hover:flex">
                                    <button
                                      onClick={() => editCaption(im)}
                                      className="rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white hover:bg-black"
                                      title="Caption"
                                    >
                                      ✎
                                    </button>
                                    <button
                                      onClick={() => removePicture(im)}
                                      className="rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white hover:bg-red-600"
                                      title="Remove"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {(editable || it.assumption) && (
                          <input
                            value={it.assumption || ""}
                            onChange={(e) =>
                              setItem(it.id, { assumption: e.target.value })
                            }
                            readOnly={!editable}
                            placeholder='Our assumption if not answered before bid close (optional) — e.g. 3-5/8" 25ga @ 16" o.c. to u/s deck'
                            className={cls(
                              inp,
                              "text-[12px]",
                              !editable &&
                                "border-transparent bg-amber-500/5 text-amber-100",
                            )}
                          />
                        )}
                        {draft.status !== "draft" && (
                          <div className="rounded-lg bg-black/25 p-2">
                            {it.answered && respFor[it.id] === undefined ? (
                              <div className="flex items-start gap-2">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                                  Answer
                                </span>
                                <div className="min-w-0 flex-1 whitespace-pre-wrap text-[12.5px] text-emerald-50">
                                  {it.response}
                                </div>
                                <button
                                  onClick={() =>
                                    setRespFor((p) => ({
                                      ...p,
                                      [it.id]: it.response,
                                    }))
                                  }
                                  className="text-[10.5px] text-gray-500 hover:text-gray-300"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <textarea
                                  rows={2}
                                  value={respFor[it.id] ?? ""}
                                  onChange={(e) =>
                                    setRespFor((p) => ({
                                      ...p,
                                      [it.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Paste the consultant's answer…"
                                  className={cls(inp, "text-[12px]")}
                                />
                                <button
                                  onClick={() => saveResponse(it.id)}
                                  disabled={!(respFor[it.id] || "").trim()}
                                  className="self-end rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
                                >
                                  Save answer
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {editable && (
                  <button
                    onClick={addBlank}
                    className="w-full rounded-xl border border-dashed border-white/15 px-3 py-2 text-xs text-gray-400 hover:border-orange-500/40 hover:text-orange-200"
                  >
                    + Add a question
                  </button>
                )}
              </div>

              {/* log */}
              <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  History
                </h3>
                <div className="space-y-1.5">
                  {[...(draft.log || [])].reverse().map((l, i) => (
                    <div key={i} className="flex gap-3 text-[12px]">
                      <span className="w-28 shrink-0 text-gray-500">
                        {new Date(l.at).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                      <span
                        className={cls(
                          "w-16 shrink-0 text-[10px] font-semibold uppercase",
                          l.kind === "response"
                            ? "text-emerald-400"
                            : l.kind === "sent"
                              ? "text-sky-300"
                              : "text-gray-500",
                        )}
                      >
                        {l.kind}
                      </span>
                      <span className="min-w-0 flex-1 whitespace-pre-wrap text-gray-300">
                        {l.text}
                        {l.by ? (
                          <span className="text-gray-600"> — {l.by}</span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    value={logText}
                    onChange={(e) => setLogText(e.target.value)}
                    placeholder="A note, or a response that covers the whole RFI…"
                    className={inp}
                  />
                  <button
                    onClick={() => addLog("note")}
                    disabled={!logText.trim()}
                    className="rounded-lg bg-white/10 px-3 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-40"
                  >
                    Note
                  </button>
                  {draft.status !== "draft" && (
                    <button
                      onClick={() => addLog("response")}
                      disabled={!logText.trim()}
                      className="whitespace-nowrap rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
                    >
                      Response
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* suggestions */}
        <aside className="flex min-h-0 flex-col border-l border-white/10 bg-gray-900/40">
          <div className="border-b border-white/10 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="text-[13px] font-semibold text-white">
                Suggested questions
              </div>
              {tab === "drawing" && (
                <button
                  onClick={refreshDrawing}
                  className="ml-auto text-[11px] text-gray-500 hover:text-gray-300"
                  title="Check the drawing again"
                >
                  ↻ Re-check
                </button>
              )}
            </div>
            <div className="mt-2 flex gap-1 text-[11px]">
              {[
                [
                  "drawing",
                  `Drawings (${drawing.filter((x) => !usedKeys.has(x.key)).length})`,
                ],
                [
                  "spec",
                  `Spec (${specIdeas.filter((x) => !usedKeys.has(x.key)).length})`,
                ],
                [
                  "inbox",
                  `From scanner (${inbox.filter((x) => !usedKeys.has(x.key)).length})`,
                ],
              ].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={cls(
                    "rounded-md px-2 py-1 font-medium",
                    tab === k
                      ? "bg-orange-500 text-white"
                      : "text-gray-400 hover:bg-white/5",
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {tab === "drawing" && !getDrawing?.() && (
              <p className="text-xs text-gray-500">
                Open a takeoff to check its drawings.
              </p>
            )}
            {tab === "spec" && !specIdeas.length && (
              <p className="text-xs text-gray-500">
                Upload the spec in the Spec scanner — the questions it finds
                (and the AI summaries') show here.
              </p>
            )}
            {ideas.map((s) => {
              const used = usedKeys.has(s.key);
              return (
                <div
                  key={s.key}
                  className={cls(
                    "rounded-xl border px-3 py-2",
                    used
                      ? "border-white/5 opacity-50"
                      : "border-white/10 bg-gray-900/70",
                  )}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={cls(
                        "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                        SEV[s.severity] || SEV.med,
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] leading-snug text-gray-100">
                        {s.question}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-gray-500">
                        {s.source?.label && <span>{s.source.label}</span>}
                        {s.why && <span>· {s.why}</span>}
                        {s.pin && onShowPin && (
                          <button
                            onClick={() => onShowPin({ ...s.pin, takeoffId })}
                            className="text-orange-300/80 hover:text-orange-200"
                          >
                            · show
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      disabled={used}
                      onClick={async () => {
                        await addSuggestion(s);
                        if (tab === "inbox") onInboxTaken?.([s.key]);
                      }}
                      className="shrink-0 rounded-md bg-orange-500/15 px-2 py-1 text-[11px] font-semibold text-orange-200 hover:bg-orange-500/25 disabled:opacity-40"
                    >
                      {used ? "✓" : "+ Add"}
                    </button>
                  </div>
                </div>
              );
            })}
            {tab === "drawing" && getDrawing?.() && !drawing.length && (
              <p className="text-xs text-emerald-300">
                Nothing missing found on the drawings.
              </p>
            )}
          </div>
        </aside>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f && pickFor.current) startPicture(pickFor.current, f);
        }}
      />
      {markup && (
        <ImageMarkup
          src={markup.src}
          title="Mark up the picture"
          sub={`${rfiNo(draft)} · question ${(draft?.items || []).findIndex((x) => x.id === markup.itemId) + 1}`}
          onCancel={() => setMarkup(null)}
          onSave={savePicture}
          saving={savingPic}
        />
      )}
      {big && <BigPicture im={big} onClose={() => setBig(null)} />}
      {sendOpen && draft && (
        <div
          className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/70 p-4 pt-[10vh]"
          onMouseDown={() => setSendOpen(false)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="border-b border-white/10 px-4 py-3">
              <div className="text-sm font-semibold text-white">
                Send {rfiNo(draft)}
              </div>
              <div className="text-[11px] text-gray-500">
                Opens your e-mail with the questions filled in. Attach the PDF
                from “Print / PDF” for the formal copy.
              </div>
            </div>
            <div className="grid gap-3 p-4 sm:grid-cols-2">
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  To (e-mail)
                </span>
                <input
                  type="email"
                  value={draft.to?.email || ""}
                  onChange={(e) =>
                    edit({ to: { ...draft.to, email: e.target.value } })
                  }
                  className={inp}
                  autoFocus
                />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Name
                </span>
                <input
                  value={draft.to?.name || ""}
                  onChange={(e) =>
                    edit({ to: { ...draft.to, name: e.target.value } })
                  }
                  className={inp}
                />
              </label>
              <label className="sm:col-span-2">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  CC (comma separated)
                </span>
                <input
                  value={(draft.cc || []).join(", ")}
                  onChange={(e) =>
                    edit({
                      cc: e.target.value.split(/[,;]\s*/).filter(Boolean),
                    })
                  }
                  className={inp}
                />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Answer needed by
                </span>
                <input
                  type="date"
                  value={dayStr(draft.dueDate)}
                  onChange={(e) => edit({ dueDate: e.target.value || null })}
                  className={inp}
                />
              </label>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button
                onClick={() => setSendOpen(false)}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Cancel
              </button>
              <button
                onClick={doPrint}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Print / PDF
              </button>
              <button
                onClick={() => doSend({ openMail: false })}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Mark as sent
              </button>
              <button
                onClick={() => doSend({ openMail: true })}
                className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
              >
                Open e-mail & mark sent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
